/*@flow*/
import {
  useEffect,
  createRef,
  useState,
  useContext,
  createContext,
} from 'react';
import * as db from 'lib/db';
import Present from 'lib/Present';

export class AutocompleteApi {
  inst: Goog$Autocomplete;
  ref: {| current: null | HTMLElement |};

  constructor(ref: {| current: null | HTMLElement |}) {
    this.ref = ref;
    this.country = db.read('country') || this.country;
    this.start();
  }

  country: any | string | Array<string> = ['us', 'ca', 'uk'];

  setCountry(country: string | string[]) {
    this.country = country;
    this.inst.setComponentRestrictions({ country: this.country });
  }

  setBounds(bbox: string) {
    if (!window.google) return;
    const gm = window.google.maps;
    const [swLng, swLat, neLng, neLat] = bbox.split(',');
    const sw = new gm.LatLng(swLat, swLng);
    const ne = new gm.LatLng(neLat, neLng);
    const bounds = new gm.LatLngBounds(sw, ne);
    this.inst.setBounds(bounds);
  }

  onChange(fn: (Goog$PlaceT, Goog$Autocomplete) => any) {
    if (!window.google) return;
    const gm = window.google.maps;
    if (!this.inst) return;
    if (!gm) return;
    gm.event.addListener(this.inst, 'place_changed', () => {
      fn(this.inst.getPlace(), this.inst);
    });
  }

  cleanup() {
    try {
      this.inst && window.google.maps.event.clearInstanceListeners(this.inst);
    } catch (_) {} //eslint-disable-line
  }

  isStarted(): boolean {
    return Boolean(this.inst);
  }

  start() {
    if (!window.google) return;
    const gm = window.google.maps;
    const el = this.ref.current;
    if (!el) return;
    this.inst = new gm.places.Autocomplete((el: any), {
      types: ['address'],
      componentRestrictions: { country: this.country },
    });
  }
}

type ResultT = {
  googPlace: $Shape<Goog$PlaceT>,
  apiRef: {| current: null | AutocompleteApi |},
};
export const AutocompleteContext: React$Context<ResultT> = createContext<ResultT>(
  {}
);

export default function useAutocomplete(
  ref: {| current: null | HTMLElement |},
  observed: any[] = []
): ResultT {
  const [googPlace, setPlace] = useState<$Shape<Goog$PlaceT>>({});
  const [apiRef, setApiRef] = useState(createRef<AutocompleteApi>());
  const ctx = useContext(AutocompleteContext);

  useEffect(() => {
    setApiRef((apiRef0) => {
      apiRef0.current = new AutocompleteApi(ref);
      return apiRef0;
    });
  }, [ref.current].concat(observed));

  useEffect(() => {
    if (!apiRef.current) return;
    apiRef.current.onChange((gp) => setPlace(gp));
    return () => {
      apiRef.current && apiRef.current.cleanup();
    };
  }, [apiRef.current]);

  return Present.is(ctx) ? ctx : { googPlace, apiRef };
}
